import { useMemo, useState } from 'react';

import { getReferralCodeFromUrl } from 'modules/referralProgram/utils/getReferralCodeFromUrl';
import { useImagePreloader } from 'modules/common/hooks/useImagePreloader';
import { useReferralProgram } from 'modules/referralProgram/hooks/useReferralProgram';
import { useSSOAuth } from 'modules/auth/hooks/useSSOAuth';

import { IReferralFlowProps } from '../ReferralFlow';
import { useIneligibleAccountDialog } from '../../IneligibleAccountDialog';
import { useInitReferralFlow } from './useInitReferralFlow';
import { useInitReferralFlowWithSavedCode } from './useInitReferralFlowWithSavedCode';
import { useSuccessDialog } from '../../SuccessDialog';
import { useSwitchAccountDialog } from '../../SwitchAccountDialog';
import { useWelcomeDialog } from '../../WelcomeDialog';

export const useReferralFlow = () => {
  const { referralCode } = getReferralCodeFromUrl();

  const { handleSignIn } = useSSOAuth({ referralCode });

  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const { cover, referralProgram } = useReferralProgram();

  const { isLoaded: isCoverLoaded } = useImagePreloader({ src: cover });

  const { handleSuccessDialogOpen, successDialogProps } = useSuccessDialog({
    cover,
    referralProgram,
  });

  const { handleIneligibleAccountDialogOpen, ineligibleAccountDialogProps } =
    useIneligibleAccountDialog({
      cover,
      referralProgram,
      setIsLoggingOut,
    });

  const { handleWelcomeDialogOpen, welcomeDialogProps } = useWelcomeDialog({
    cover,
    handleSignIn,
    handleSuccessDialogOpen,
    referralProgram,
  });

  const { handleSwitchAccountDialogOpen, switchAccountDialogProps } =
    useSwitchAccountDialog({
      cover,
      referralProgramName: referralProgram?.name,
    });

  const referralFlowProps = useMemo(
    (): IReferralFlowProps => ({
      ineligibleAccountDialogProps,
      successDialogProps,
      switchAccountDialogProps,
      welcomeDialogProps,
    }),
    [
      ineligibleAccountDialogProps,
      successDialogProps,
      switchAccountDialogProps,
      welcomeDialogProps,
    ],
  );

  useInitReferralFlow({
    handleIneligibleAccountDialogOpen,
    handleSwitchAccountDialogOpen,
    handleWelcomeDialogOpen,
    isCoverLoaded,
    isLoggingOut,
  });

  // to init referral flow after oauth logging in
  useInitReferralFlowWithSavedCode({
    handleIneligibleAccountDialogOpen,
    handleSuccessDialogOpen,
    handleSwitchAccountDialogOpen,
    isCoverLoaded,
  });

  return { referralFlowProps };
};
